/**
 * Which queued CRUD rows the connector may already have sent: their changes
 * may be on the server even though they are still in `ps_crud`.
 *
 * A row stays in `ps_crud` until `transaction.complete()` runs, after the
 * network request. Code that treats a queued PUT as "never reached the
 * server" (e.g. deleteObservationWithDatabase suppressing the DELETEs a local
 * delete generates) must check here first, or a delete during an upload
 * leaves the row on the server and it comes back on the next sync (#211).
 *
 * Kept as a high-water mark: the largest `ps_crud` id the connector has read
 * to upload. `ps_crud.id` is AUTOINCREMENT and the queue is FIFO, so every
 * row still queued at or below the mark has been read, and so possibly sent,
 * but not completed (completing removes rows from `ps_crud`). That covers a
 * failed attempt too, since a request that timed out may still have landed.
 * The mark is saved to localStorage as it moves, so it survives an app
 * restart, and is reset whenever the connector finds the queue empty.
 * Erring high is harmless: the delete keeps a DELETE that matches nothing.
 *
 * Cross-tab: PowerSync's shared client runs the upload loop in one tab while
 * the user can delete in another, and which tab uploads can change when the
 * leader lock moves. Tabs share a database but not module state, so the mark
 * is read back from localStorage on every check rather than trusted from the
 * copy loaded at startup, and the lock below is a Web Lock
 * (`navigator.locks`), which is per-origin and spans tabs. Without that,
 * another tab's delete sees a stale mark of 0 and suppresses the DELETEs a
 * create that is uploading still needs.
 *
 * withUploadMarkLock makes "read a transaction and mark it" atomic relative
 * to a delete's write transaction, in any tab. On the default web build
 * reads and writes share one connection, so they can't interleave within a
 * tab anyway, but the native driver reads on a separate WAL connection, and
 * a delete in another tab is always a different connection.
 *
 * Kept free of imports so deletion code can read it without pulling in the
 * connector's dependencies.
 */

const STORAGE_KEY = "powersync_upload_read_through"
const LOCK_NAME = "fieldbook-powersync-upload-mark"

/** The parts of a PowerSync CrudTransaction this module needs. */
type ReadTransaction = {
  crud: ReadonlyArray<{ clientId?: number }>
}

/** The slice of the Web Locks API used here. */
type ExclusiveLockManager = {
  request: <T>(
    name: string,
    options: { mode: "exclusive" },
    callback: () => Promise<T>,
  ) => Promise<T>
}

function loadStoredMark(): number {
  try {
    const parsed = Number(localStorage.getItem(STORAGE_KEY))
    return Number.isFinite(parsed) ? parsed : 0
  } catch {
    return 0
  }
}

function saveMark(): void {
  try {
    localStorage.setItem(STORAGE_KEY, String(readThrough))
  } catch {
    // Kept in memory regardless; only a restart would lose it, and then only
    // for uploads this tab already started.
  }
}

let readThrough = loadStoredMark()

/**
 * The highest mark recorded, by this tab or another one. The stored value is
 * read every time: a tab that only deletes never loads the mark itself, and
 * the uploading tab can move it at any moment.
 */
function highestMark(): number {
  const stored = loadStoredMark()
  if (stored > readThrough) readThrough = stored
  return readThrough
}

/**
 * Record that the connector has read `transaction` from the queue to upload.
 * Call it in the same synchronous step as the read, inside
 * withUploadMarkLock, so a delete can never see its rows queued but not yet
 * marked.
 */
export function markUploadRead(transaction: ReadTransaction): void {
  const last = transaction.crud[transaction.crud.length - 1]?.clientId
  if (last === undefined || last <= highestMark()) return
  readThrough = last
  saveMark()
}

/**
 * Record that the queue is empty: nothing queued can have been sent. Resets
 * the mark, so it can't outlive a database whose `ps_crud` ids restarted.
 * Takes the lock so a delete's write transaction, which may be reading the
 * mark, can't be reset mid-decision.
 */
export async function markUploadQueueEmpty(): Promise<void> {
  await withUploadMarkLock(async () => {
    if (highestMark() === 0) return
    readThrough = 0
    saveMark()
  })
}

/** Whether the queued `ps_crud` row with this id may already have been sent. */
export function isCrudRowUnconfirmed(crudId: number): boolean {
  return crudId <= highestMark()
}

function crossTabLocks(): ExclusiveLockManager | null {
  if (typeof navigator === "undefined") return null
  const locks = (navigator as Navigator & { locks?: unknown }).locks
  if (typeof locks !== "object" || locks === null) return null
  const { request } = locks as ExclusiveLockManager
  return typeof request === "function" ? (locks as ExclusiveLockManager) : null
}

let lockTail: Promise<void> = Promise.resolve()

/** Order calls from this module instance only: the fallback for no Web Locks. */
async function withInProcessLock<T>(fn: () => Promise<T>): Promise<T> {
  const previous = lockTail
  let release = () => {}
  lockTail = new Promise((resolve) => {
    release = resolve
  })
  await previous
  try {
    return await fn()
  } finally {
    release()
  }
}

/**
 * Run `fn` while no other caller of this function is running, in this tab or
 * any other. Take it before any database lock (the connector around its
 * queue read and mark, the delete around its whole write transaction), never
 * while holding one.
 *
 * Where Web Locks are missing (older Safari, non-secure contexts, tests) it
 * falls back to an in-process queue: the persisted mark is still shared, but
 * a read and a delete in two tabs can then interleave.
 */
export async function withUploadMarkLock<T>(fn: () => Promise<T>): Promise<T> {
  const locks = crossTabLocks()
  if (!locks) return withInProcessLock(fn)
  return locks.request(LOCK_NAME, { mode: "exclusive" }, () =>
    withInProcessLock(fn),
  )
}

/** For tests: forget the mark. */
export function resetUnconfirmedUploads(): void {
  readThrough = 0
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    // Ignore
  }
}
